import { useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { useGetHistoryQuery } from "@api/auditLog.api";
import { ResourceType } from "@common/types/auditLog";
import {
    getHistoryRowId,
    historyItemToRow,
} from "@components/history/utils/historyItemToRow";
import { Button } from "@design-system/Button/Button";
import { Loader } from "@design-system/Loader/Loader";
import { PanelEmptyMessage } from "@design-system/PanelEmptyMessage/PanelEmptyMessage";
import { SubScopeProvider } from "@state/subScopeContext";
import { WorkActivityLine } from "./WorkActivityLine";
import styles from "./WorkActivity.module.scss";

const PAGE_SIZE = 5;

interface WorkActivitySectionProps {
    closeId: string;
    workspaceId: string;
}

/**
 * The work item's activity, read from its client workspace's history: the
 * work item's own edits and its tasks' changes, grouped as on the Activity
 * page, newest first, five lines at a time. Lines that "Load more" adds fade
 * in one after another.
 */
export const WorkActivitySection = ({
    closeId,
    workspaceId,
}: Readonly<WorkActivitySectionProps>) => (
    <SubScopeProvider value={{ workspaceId }}>
        <WorkActivityFeed closeId={closeId} workspaceId={workspaceId} />
    </SubScopeProvider>
);

const WorkActivityFeed = ({
    closeId,
    workspaceId,
}: Readonly<WorkActivitySectionProps>) => {
    const [page, setPage] = useState(1);
    const { data, isPending, isError, isPlaceholderData } = useGetHistoryQuery({
        relatedResource: { type: ResourceType.CLOSE, id: closeId },
        page: 1,
        limit: page * PAGE_SIZE,
        options: { placeholderData: keepPreviousData },
    });

    if (isPending) {
        return <Loader inline />;
    }
    if (isError) {
        return <PanelEmptyMessage>Failed to load activity</PanelEmptyMessage>;
    }
    if (data.data.length === 0) {
        return <PanelEmptyMessage>No activity yet</PanelEmptyMessage>;
    }

    const rows = data.data.map((item) =>
        historyItemToRow({ item, id: getHistoryRowId(item) }),
    );
    const canLoadMore = data.total > rows.length;

    return (
        <div className={styles.activity}>
            <ul className={styles.lines}>
                {rows.map((row, index) => (
                    <WorkActivityLine
                        key={row.id}
                        row={row}
                        closeId={closeId}
                        workspaceId={workspaceId}
                        staggerIndex={
                            index < PAGE_SIZE ? undefined : index % PAGE_SIZE
                        }
                    />
                ))}
            </ul>
            {canLoadMore && (
                <Button
                    variant="secondary"
                    size="sm"
                    disabled={isPlaceholderData}
                    onClick={() => setPage(page + 1)}
                >
                    {isPlaceholderData ? "Loading…" : "Load more"}
                </Button>
            )}
        </div>
    );
};
